import { lerp } from "common/utils/math";
import type { DmBlendMode } from "../designMaterialTypes";

export function blendModeInt(blendModeType: DmBlendMode) {
  switch (blendModeType) {
    case "lighten":
    case "darken":
      return 1;
    case "difference":
      return 2;
    case "legacy-screen":
      return 3;
    case "alpha":
    case "multiply":
    case "screen":
    case "add":
    case "subtract":
    default:
      return 0;
  }
}

export function blendModeCoefficients(blendModeType: DmBlendMode) {
  // Blending coefficients for:
  // 1. source color
  // 2. destination color
  // 3. product of source color and destination color
  switch (blendModeType) {
    case "multiply":
      return [0, 0, 1];
    case "screen":
    case "legacy-screen":
      return [1, 1, -1];
    case "add":
      return [1, 1, 0];
    case "subtract":
    case "difference":
      return [-1, 1, 0];
    case "lighten":
    case "darken":
    case "alpha":
    default:
      return [1, 0, 0];
  }
}

export type RGBArray = [number, number, number];

/**
 * Software implementation of the blend modes matching the shader code.
 * Note that this function does not clamp the result to the 0-1 range, since the shader doesn't either.
 * @param targetColor The color to blend onto, in the 0-1 range.
 * @param srcColor The color to blend with, in the 0-1 range.
 * @param blendStrength Alpha value to use for blending.
 * @param blendMode The blend mode to use.
 * @returns The blended color.
 */
export function blendColors(
  targetColor: RGBArray,
  srcColor: RGBArray,
  blendStrength: number,
  blendMode: DmBlendMode,
): RGBArray {
  let blendResult: RGBArray = [0, 0, 0];
  switch (blendMode) {
    case "alpha":
      blendResult = [srcColor[0], srcColor[1], srcColor[2]];
      break;
    case "multiply":
      blendResult = [targetColor[0] * srcColor[0], targetColor[1] * srcColor[1], targetColor[2] * srcColor[2]];
      break;
    case "screen":
      blendResult = [
        targetColor[0] + srcColor[0] - targetColor[0] * srcColor[0],
        targetColor[1] + srcColor[1] - targetColor[1] * srcColor[1],
        targetColor[2] + srcColor[2] - targetColor[2] * srcColor[2],
      ];
      break;
    case "legacy-screen":
      blendResult = [
        (targetColor[0] + srcColor[0] - targetColor[0] * srcColor[0]) * srcColor[0],
        (targetColor[1] + srcColor[1] - targetColor[1] * srcColor[1]) * srcColor[1],
        (targetColor[2] + srcColor[2] - targetColor[2] * srcColor[2]) * srcColor[2],
      ];
      break;
    case "add":
      // Note that this does not clamp the result, since the shader doesn't either.
      blendResult = [targetColor[0] + srcColor[0], targetColor[1] + srcColor[1], targetColor[2] + srcColor[2]];
      break;
    case "subtract":
      blendResult = [targetColor[0] - srcColor[0], targetColor[1] - srcColor[1], targetColor[2] - srcColor[2]];
      break;
    case "difference":
      blendResult = [
        Math.abs(targetColor[0] - srcColor[0]),
        Math.abs(targetColor[1] - srcColor[1]),
        Math.abs(targetColor[2] - srcColor[2]),
      ];
      break;
    case "darken":
      blendResult = [
        Math.min(targetColor[0], srcColor[0]),
        Math.min(targetColor[1], srcColor[1]),
        Math.min(targetColor[2], srcColor[2]),
      ];
      break;
    case "lighten":
      blendResult = [
        Math.max(targetColor[0], srcColor[0]),
        Math.max(targetColor[1], srcColor[1]),
        Math.max(targetColor[2], srcColor[2]),
      ];
      break;
  }
  return [
    lerp(targetColor[0], blendResult[0], blendStrength),
    lerp(targetColor[1], blendResult[1], blendStrength),
    lerp(targetColor[2], blendResult[2], blendStrength),
  ];
}

/**
 * /**
 * @param blendMode should be a variable name containing a value returned from blendModeInt.
 * @param blendCoefficients should be a variable name containing a value returned from blendModeCoefficients, defining a blend mode as a linear combination of different color values.
 * @param blendTarget the variable name where the blend result is saved.
 * @param blendSrc the variable name for the source color. The value of blendSrc can be changed by the returned code.
 * @param blendStrength alpha value to use for blending
 * @returns Code to blend two colors.
 */
export function dynamicBlendingChunk(
  blendMode: string,
  blendCoefficients: string,
  blendTarget: string,
  blendSrc: string,
  blendStrength: string,
) {
  // In most cases, the code first sets blendSrc to the result of blending blendSrc to blendTarget with 100% strength,
  // and then mixes the resulting blendSrc to blendTarget according to blendStrength.
  return `
  if (${blendMode} == 1) { // lighten / darken
    ${blendTarget}.rgb = ${blendTarget}.rgb + sign(${blendStrength}) * max((${blendSrc}.rgb - ${blendTarget}.rgb) * ${blendStrength}, 0.0);
  } else {
    mat3 blendComponents = mat3(${blendSrc}.rgb, ${blendTarget}.rgb, ${blendSrc}.rgb * ${blendTarget}.rgb);
    vec3 blendCombination = blendComponents * ${blendCoefficients};
    if (${blendMode} == 3) { // legacy-screen
      blendCombination *= ${blendSrc}.rgb;
    } else if (${blendMode} == 2) { // difference
      blendCombination = abs(blendCombination);
    }
    ${blendTarget}.rgb = mix(${blendTarget}.rgb, blendCombination, ${blendStrength});
  }`;
}
